import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, Subscription } from 'rxjs';
import { HttpInsuranceProductService } from '../../../../service/http-insurance-product.service';
import { InsurancePremiumRecipientDto, PolicyDto, ProductCommissionTypeEnum, ProductDto, ProductOfferDto } from '../../../../service/dto-insurance';
import { ResultListDto } from '../../../../service/dto';
import { Filter, OnTableChange, TableColumn } from '../../../../components/table/table.component';

@Component({
	selector: 'app-product-view',
	standalone: false,
	// template:'',
	templateUrl: './product-view.component.html',
	styleUrl: './product-view.component.scss'
})
export class ProductViewComponent {// implements OnInit, OnDestroy {
	subscription = new Subscription();
	product?: ProductDto;
	loading = true;
	selectedTab = 1;

	policies?: ResultListDto<PolicyDto>;
	policyLoading = false;
	policyTableState: OnTableChange = { pageIndex: 0, pageSize: 0, filters: [] };
	policyFilters: Filter[] = [
		// { label: 'ID Number', name: 'idNumber', type: 'Text', format: '^\\w+$' },
		// { label: 'Name', name: 'name', type: 'Text', format: '^\\w.*$' },
		// { label: 'Surname', name: 'surname', type: 'Text', format: '^\\w.*$' },
		// { label: 'Msisdn', name: 'msisdn', type: 'Text', format: '^\\w.*$' },
		// { label: 'Email Address', name: 'emailAddress', type: 'Text', format: '^\\w.*$' },
		// { label: 'Membership Number', name: 'membershipNumber', type: 'Text', format: '^\\w.*$' }
	];
	policyColumnRef: TableColumn[] = [
		{ "title": "Policy Number", "property": "policyNumber" },
		{ "title": "Name", "property": "member.name" },
		{ "title": "Surname", "property": "member.surname" },
		{ "title": "ID Number", "property": "member.idNumber" },
		{ "title": "Premium", "property": "premiumAmount" },
		{ "title": "Status", "property": "status" }
	];
	offerCommissionRecipients: InsurancePremiumRecipientDto[] = [];

	constructor(private activatedRoute: ActivatedRoute, private router: Router, private httpService: HttpInsuranceProductService) { }

	ngOnInit(): void {
		this.subscription.add(this.activatedRoute.paramMap.subscribe(paramMap => {
			this.loading = true;
			this.httpService.get(+paramMap.get('productId')!)
				.pipe(finalize(() => this.loading = false))
				.subscribe(result => {
					this.product = result;
					this.loading = false;


					const recipientSet = new Set<InsurancePremiumRecipientDto>();
					for (const offer of this.product.offers ?? []) {
						for (const line of offer.lines ?? []) {
							for (const detail of line.details ?? []) {
								for (const commission of detail.commissions ?? []) {
									if (commission.recipient?.name) {
										recipientSet.add(commission.recipient);
									}
								}
							}
						}
					}
					this.offerCommissionRecipients = [...recipientSet];

				});
		}))
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	selectedPolicyTab() {
		if (!this.policies) {
			this.doFetchPolicyResults({ pageIndex: 1, pageSize: 30, filters: [] });
		}
	}


	doFetchPolicyResults(input: OnTableChange) {
		if (JSON.stringify(this.policyTableState) == JSON.stringify(input)) {
			return;
		}
		this.policyTableState = input;
		this.policyLoading = true;


		this.httpService.getPolicies(this.product!.id, this.policyTableState.pageIndex, this.policyTableState.pageSize)
			.pipe(finalize(() => {
				this.policyLoading = false;
			}))
			.subscribe(result => {
				this.policies = result;
			});
	}
	openSchedule() {
		// 	this.router.navigate(['/product',this.product!.id,'schedule','list']);
		this.router.navigate(['schedule', 'list'], { relativeTo: this.activatedRoute });
	}
	openClaimWorkflow() {
		// 	this.router.navigate(['/product',this.product!.id,'workflow']);
		this.router.navigate(['workflow'], { relativeTo: this.activatedRoute });
	}


	/** Sum of fixed premiums across all compulsory lines — returns null if none */
	getOfferFixedPremium(offer: ProductOfferDto): number | null {
		let total = 0;
		let found = false;
		for (const line of offer.lines ?? []) {
			if (!line.compulsory) continue;
			for (const detail of line.details ?? []) {
				if (detail.premiumType === 'Fixed' && detail.premiumAmount) {
					total += detail.premiumAmount;
					found = true;
				}
			}
		}
		return found ? total : null;
	}

	/** Percentage premium label — returns null if none */
	getOfferPercentagePremium(offer: ProductOfferDto): string | null {
		const props = new Set<string>();
		for (const line of offer.lines ?? []) {
			if (!line.compulsory) continue;
			for (const detail of line.details ?? []) {
				if (detail.premiumType === 'Percentage' && detail.premiumPercentageProperty) {
					props.add(detail.premiumPercentageProperty);
				}
			}
		}
		return props.size ? [...props].join(', ') : null;
	}

	/** Sum of fixed benefits across all compulsory lines — returns null if none */
	getOfferFixedBenefit(offer: ProductOfferDto): number | null {
		let total = 0;
		let found = false;
		for (const line of offer.lines ?? []) {
			if (!line.compulsory) continue;
			for (const detail of line.details ?? []) {
				if (detail.benefitType === 'Fixed' && detail.benefitAmount) {
					total += detail.benefitAmount;
					found = true;
				}
			}
		}
		return found ? total : null;
	}

	/** Percentage benefit label — returns null if none */
	getOfferPercentageBenefit(offer: ProductOfferDto): string | null {
		const props = new Set<string>();
		for (const line of offer.lines ?? []) {
			if (!line.compulsory) continue;
			for (const detail of line.details ?? []) {
				if (detail.benefitType === 'Percentage' && detail.benefitPercentageProperty) {
					props.add(detail.benefitPercentageProperty);
				}
			}
		}
		return props.size ? [...props].join(', ') : null;
	}

	/** Commission for a specific named recipient across all compulsory lines of the offer */
	getOfferCommission(offer: ProductOfferDto, recipient: InsurancePremiumRecipientDto): { amount: number; type: ProductCommissionTypeEnum } | null {
		let total = 0;
		let commissionType: ProductCommissionTypeEnum = 'Fixed';
		let found = false;
		for (const line of offer.lines ?? []) {
			if (!line.compulsory) continue;
			for (const detail of line.details ?? []) {
				for (const commission of detail.commissions ?? []) {
					if (commission.recipient?.id === recipient.id) {
						total += commission.commissionAmount;
						commissionType = commission.commissionType;
						found = true;
					}
				}
			}
		}
		return found ? { amount: total, type: commissionType } : null;
	}
}
